import { useCallback, useRef } from 'react'
import { useGoogleLogin } from '@react-oauth/google'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { googleDriveApi } from '../api/googleDriveApi'
import { clearGoogleDriveAccessToken, getGoogleDriveAccessToken, setGoogleDriveAccessToken } from '../services/googleDriveSession'
import { resetGoogleDriveState, setGoogleDriveState } from '../store/googleDriveSlice'
import { clearGoogleDriveFolderCache } from '../services/googleDriveStorage'

let refreshPromise: Promise<string | null> | null = null

export function useGoogleDriveAuth() {
  const dispatch = useAppDispatch()
  const state = useAppSelector((root) => root.googleDrive)
  const pendingRef = useRef<{ resolve: (token: string) => void; reject: (error: Error) => void } | null>(null)

  const refreshAccess = useCallback(async (force = false): Promise<string | null> => {
    const current = getGoogleDriveAccessToken()
    if (current && !force) return current
    if (refreshPromise) return refreshPromise

    dispatch(setGoogleDriveState({ initialized: true, status: 'refreshing' }))
    refreshPromise = googleDriveApi.refresh().then((response) => {
      if (!response.success || !response.data?.access_token) {
        clearGoogleDriveAccessToken()
        dispatch(setGoogleDriveState({ initialized: true, status: 'required' }))
        return null
      }
      setGoogleDriveAccessToken(response.data.access_token, response.data.expires_in)
      dispatch(setGoogleDriveState({ initialized: true, status: 'connected' }))
      return response.data.access_token
    }).catch(() => {
      clearGoogleDriveAccessToken()
      dispatch(setGoogleDriveState({ initialized: true, status: 'error' }))
      return null
    }).finally(() => {
      refreshPromise = null
    })
    return refreshPromise
  }, [dispatch])

  const login = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/drive.file',
    select_account: true,
    onSuccess: (codeResponse) => {
      void googleDriveApi.exchangeCode(codeResponse.code).then((response) => {
        if (!response.success || !response.data?.access_token) {
          throw new Error(response.message || 'Không thể kết nối Google Drive.')
        }
        setGoogleDriveAccessToken(response.data.access_token, response.data.expires_in)
        dispatch(setGoogleDriveState({ initialized: true, status: 'connected' }))
        pendingRef.current?.resolve(response.data.access_token)
        pendingRef.current = null
      }).catch((error: unknown) => {
        clearGoogleDriveAccessToken()
        dispatch(setGoogleDriveState({ initialized: true, status: 'required' }))
        pendingRef.current?.reject(error instanceof Error ? error : new Error('Không thể kết nối Google Drive.'))
        pendingRef.current = null
      })
    },
    onError: () => {
      clearGoogleDriveAccessToken()
      dispatch(setGoogleDriveState({ initialized: true, status: 'required' }))
      pendingRef.current?.reject(new Error('Không thể kết nối Google Drive.'))
      pendingRef.current = null
    },
  })

  const connect = useCallback(() => new Promise<string>((resolve, reject) => {
    pendingRef.current = { resolve, reject }
    login()
  }), [login])

  const initialize = useCallback(async () => {
    if (state.initialized || state.status === 'checking' || state.status === 'refreshing') return
    dispatch(setGoogleDriveState({ status: 'checking' }))
    try {
      const response = await googleDriveApi.status()
      const connectedEmail = response.data?.google_email ?? null
      if (!response.success || !response.data?.connected) {
        clearGoogleDriveAccessToken()
        dispatch(setGoogleDriveState({ initialized: true, status: 'required', connectedEmail }))
        return
      }
      dispatch(setGoogleDriveState({ connectedEmail }))
      await refreshAccess()
    } catch {
      dispatch(setGoogleDriveState({ initialized: true, status: 'error' }))
    }
  }, [dispatch, refreshAccess, state.initialized, state.status])

  const ensureAccess = useCallback(async (interactive = false) => {
    const current = getGoogleDriveAccessToken()
    if (current) return current
    if (!interactive && state.initialized && (state.status === 'required' || state.status === 'error')) return null
    const refreshed = await refreshAccess()
    if (refreshed || !interactive) return refreshed
    return connect()
  }, [connect, refreshAccess, state.initialized, state.status])

  const reset = useCallback(() => {
    clearGoogleDriveAccessToken()
    clearGoogleDriveFolderCache()
    dispatch(resetGoogleDriveState())
  }, [dispatch])

  return { ...state, initialize, ensureAccess, refreshAccess, connect, reset }
}
